import { database, Form, TaskOverride } from "../backend/database";
import {
  ItemDto,
  PhotoDto,
  RecordingDto,
  TaskDto,
} from "../backend/models/dto/Item.dto";
import { Topic as ExcelTopic } from "../models/excel.model";

export class DatabaseService {
  public async storeNewTemplate(
    fileName: string,
    lastModified: number,
    hash: string,
    name: string,
    topics: ExcelTopic[]
  ) {
    const insertedTemplateId = await database.transaction(
      "rw",
      [
        database.templates,
        database.categories,
        database.sections,
        database.items,
        database.tasks,
      ],
      async () => {
        const templateId = await database.templates.add({
          title: name,
          fileName: fileName,
          fileCreatedAt: new Date(lastModified),
          fileHash: hash,
          createdAt: new Date(),
        });

        for (const topic of topics) {
          const categoryId = await database.categories.add({
            templateId: templateId,
            title: topic.title,
            sectionCount: topic.categories.length,
          });

          for (const category of topic.categories) {
            const sectionId = await database.sections.add({
              templateId: templateId,
              categoryId: categoryId,
              title: category.title,
              itemCount: category.requirements.length,
            });

            for (const requirement of category.requirements) {
              const itemId = await database.items.add({
                templateId: templateId,
                sectionId: sectionId,
                title: requirement.title,
                taskCount: requirement.questions.length,
              });

              for (const question of requirement.questions) {
                await database.tasks.add({
                  templateId: templateId,
                  itemId: itemId,
                  prefix: question.prefix,
                  content: question.question,
                  status: null,
                });
              }
            }
          }
        }

        return templateId;
      }
    );

    return insertedTemplateId;
  }

  public async getTemplate(templateId: number) {
    const template = await database.templates.get(templateId);

    if (!template) {
      return null;
    }

    return template;
  }

  public async getTemplateTitle(id: number) {
    return await database.templates.get(id, (a) => a?.title);
  }

  public async deleteTemplate(id: number) {
    await database.templates.delete(id);
  }

  public async createNewForm(templateId: number, templateName: string) {
    const form: Form = {
      templateId: templateId,
      templateName: templateName,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const id = await database.forms.add(form);

    form.id = id;

    return form;
  }

  public async getForm(formId: number) {
    const form = await database.forms.get(formId);

    if (!form) {
      return null;
    }

    return form;
  }

  public async getCategory(categoryId: number) {
    const category = await database.categories.get(categoryId);

    if (!category) {
      return null;
    }

    return category;
  }

  public async getCategoryFromFormOrTemplate(templateId: number) {
    const categories = await database.categories
      .where("templateId")
      .anyOf(templateId)
      .toArray();
    return categories;
  }

  public async getSectionsFromCategory(categoryId: number) {
    const categories = await database.sections
      .where("categoryId")
      .anyOf(categoryId)
      .toArray();
    return categories;
  }

  public async getSectionCountForCategories(categoryId: number[]) {
    const items = await database.sections
      .where("categoryId")
      .anyOf(categoryId)
      .toArray();
    const counts = items.reduce<Record<number, number>>((acc, item) => {
      acc[item.categoryId] = (acc[item.categoryId] ?? 0) + 1;
      return acc;
    }, {});

    return counts;
  }

  public async getItemCountForSections(sectionIds: number[]) {
    const items = await database.items
      .where("sectionId")
      .anyOf(sectionIds)
      .toArray();
    const counts = items.reduce<Record<number, number>>((acc, item) => {
      acc[item.sectionId] = (acc[item.sectionId] ?? 0) + 1;
      return acc;
    }, {});

    return counts;
  }

  public async getItems(
    sectionId: number,
    formId?: number
  ): Promise<ItemDto[]> {
    const items = await database.items
      .where("sectionId")
      .equals(sectionId)
      .toArray();

    if (items.length === 0) return [];

    const itemIds = items.map((i) => i.id!);

    const tasks = await database.tasks.where("itemId").anyOf(itemIds).toArray();

    const [overrides, photos, recordings] = formId
      ? await Promise.all([
          database.taskOverrides.where("formId").equals(formId).toArray(),
          database.photos.where("formId").equals(formId).toArray(),
          database.recordings.where("formId").equals(formId).toArray(),
        ])
      : [[], [], []];

    const overrideByTaskId = new Map<number, TaskOverride>(
      overrides.map((o) => [o.taskId, o])
    );

    const photosByTaskId = new Map<number, PhotoDto[]>();
    for (const p of photos) {
      if (!photosByTaskId.has(p.taskId)) {
        photosByTaskId.set(p.taskId, []);
      }
      photosByTaskId.get(p.taskId)!.push({
        id: p.id!,
        date: p.date,
        file: p.file,
      });
    }

    const recordingsByTaskId = new Map<number, RecordingDto[]>();
    for (const r of recordings) {
      if (!recordingsByTaskId.has(r.taskId)) {
        recordingsByTaskId.set(r.taskId, []);
      }
      recordingsByTaskId.get(r.taskId)!.push({
        id: r.id!,
        date: r.date,
        blob: r.blob,
      });
    }

    const tasksByItemId = new Map<number, TaskDto[]>();

    for (const task of tasks) {
      const override = overrideByTaskId.get(task.id!);

      const dto: TaskDto = {
        id: task.id!,
        overrideId: override?.id,
        prefix: task.prefix,
        content: task.content,
        status: override?.status ?? task.status,
        photos: photosByTaskId.get(task.id!) ?? [],
        recordings: recordingsByTaskId.get(task.id!) ?? [],
      };

      if (!tasksByItemId.has(task.itemId)) {
        tasksByItemId.set(task.itemId, []);
      }
      tasksByItemId.get(task.itemId)!.push(dto);
    }

    return [];
    items.map((item) => ({
      id: item.id!,
      templateId: item.templateId,
      title: item.title,
      tasks: tasksByItemId.get(item.id!) ?? [],
    }));
  }
}

export const databaseService = new DatabaseService();
